import { Routes } from '@angular/router';
import { authGuard, canActivateRoleGuard, mercerGuard } from '@coin/shared/auth/feature';
import { AuthGmsClientCallbackComponent, LogoutScreenComponent } from '@coin/shared/auth/feature';
import { environmentGuard } from '@coin/shared/core/feature-guards';
import { NoBackendScreenComponent } from '@coin/shared/core/feature-legacy-components';
import { FrontendType, PermissionResource } from '@coin/shared/core/util-enums';
import { environment } from '@coin/shared/core/util-environments';
import { redirectGuard } from './shared/guards/redirect.guard';
import { customerRoleGuard } from './shared/services/customer-role-guard.service';
import { ServiceProviderListComponent } from './static/service-provider-list/service-provider-list.component';
import { canActivateDeputyGuard } from './shared/guards/can-activate-deputy-guard';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';

export const APP_ROUTES: Routes = [
  {
    path: '',
    loadChildren: () => import('./home/home.routes'),
    canActivate: [authGuard],
    data: {
      frontendType: FrontendType.customer
    },
    pathMatch: 'full'
  },
  {
    path: 'videos',
    loadChildren: () => import('./videos/videos.routes'),
    canActivate: [authGuard],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'auth/callback',
    component: AuthGmsClientCallbackComponent
  },
  {
    path: 'logout',
    component: LogoutScreenComponent
  },
  {
    // The login page is gone (sign-in is automatic); old bookmarks go home, where the guard signs in.
    path: 'login',
    redirectTo: ''
  },
  {
    path: 'document-center',
    loadChildren: () => import('./document-center/document-center.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.CustomerApp
        }
      ]),
      canActivateDeputyGuard
    ],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'employee-detail-overview',
    loadChildren: () => import('./employee-detail-overview/employee-detail-overview.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.CustomerApp
        }
      ]),
      canActivateDeputyGuard
    ],
    canMatch: [environmentGuard],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'document-download',
    loadChildren: () => import('./document-download/document-download.routes'),
    canActivate: [authGuard],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'budget-allocation',
    loadChildren: () => import('./merit/merit-budget-allocation/merit-budget-allocation.routes'),
    canActivate: [authGuard],
    canMatch: [environmentGuard],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'gbr',
    loadChildren: () => import('./gbr/gbr.routes'),
    canActivate: [authGuard],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'special-payment',
    redirectTo: 'special-recognition'
  },
  {
    path: 'special-recognition',
    loadChildren: () => import('./special-payment/special-payment.routes'),
    canActivate: [authGuard, customerRoleGuard],
    data: {
      allowedResources: [
        {
          resource: PermissionResource.SpecialPayment
        }
      ],
      directAllowAccessLineManager: true,
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'support-apps',
    loadChildren: () => import('./merit/hr-partner-app/hr-partner-app.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.AllocationHR
        },
        {
          resource: PermissionResource.ReOrganisationApp
        },
        {
          resource: PermissionResource.PositionEvaluationHR
        },
        {
          resource: PermissionResource.OrganisationManagementAdmin
        },
        {
          resource: PermissionResource.OrganisationPlanningAdmin
        },
        {
          resource: PermissionResource.OrganisationPlanningSupport
        },
        {
          resource: PermissionResource.OrganisationPlanningFinanceSupport
        },
        {
          resource: PermissionResource.SuccessionManagementAdmin
        },
        {
          resource: PermissionResource.SuccessionManagementHR
        },
        {
          resource: PermissionResource.IncentiveSeasonHr
        }
      ])
    ],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'org-review',
    loadChildren: () => import('./org-review/org-review.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.ReOrganisationApp
        }
      ])
    ],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'merit-support',
    loadChildren: () => import('./merit/merit-support/merit-support.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.AllocationHR
        }
      ])
    ],
    canMatch: [environmentGuard],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'mercer',
    loadChildren: () => import('./mercer/mercer.routes'),
    canActivate: [authGuard, canActivateRoleGuard(), mercerGuard],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'org-planning',
    loadChildren: () => import('./org-planning/org-planning.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.OrganisationPlanning
        },
        {
          resource: PermissionResource.OrganisationPlanningAdmin
        }
      ])
    ],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'org-management',
    loadChildren: () => import('@coin/customer/org-management/feature').then(m => m.ORG_MANAGEMENT_ROUTES),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.OrganisationManagementUser
        },
        {
          resource: PermissionResource.OrganisationManagementAdmin
        }
      ]),
      environmentGuard
    ],
    data: {
      frontendType: FrontendType.customer,
      featureFlag: 'orgManagement'
    }
  },
  {
    path: 'org-planning-support',
    loadChildren: () => import('./org-planning-support/org-planning-support.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.OrganisationPlanningAdmin
        },
        {
          resource: PermissionResource.OrganisationPlanningSupport
        },
        {
          resource: PermissionResource.OrganisationPlanningFinanceSupport
        }
      ])
    ],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'mercer-support',
    loadChildren: () => import('./mercer-support/mercer-support.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.PositionEvaluationHR
        }
      ])
    ],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'mercer-standing',
    loadChildren: () => import('./mercer-standing/mercer-standing.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.StandingPositionEvaluationHR
        }
      ]),
      environmentGuard
    ],
    data: {
      frontendType: FrontendType.customer,
      featureFlag: 'mercerStanding'
    }
  },
  {
    // Subpath /headcount-approval was renamed to /people-planner. Redirect everything going to old subpath to the new one.
    path: 'headcount-approval',
    redirectTo: 'people-planner'
  },
  {
    path: 'people-planner',
    loadChildren: () => import('./headcount-approval/headcount-approval.routes'),
    canActivate: [authGuard, customerRoleGuard, environmentGuard],
    data: {
      allowedResources: [
        {
          resource: PermissionResource.CAndB
        },
        {
          resource: PermissionResource.HeadcountApprovalHr
        },
        {
          resource: PermissionResource.HeadcountApprovalAdmin
        },
        {
          resource: PermissionResource.HeadcountApprovalUser
        },
        {
          resource: PermissionResource.HeadcountApprovalGidApprover
        },
        {
          resource: PermissionResource.HeadcountController
        }
      ],
      frontendType: FrontendType.customer,
      directAllowAccessLineManager: true,
      directAllowAccessHead: true
    }
  },
  {
    path: 'succession-management',
    loadChildren: () => import('./succession-management/succession-management.routes'),
    canActivate: [authGuard, customerRoleGuard, environmentGuard],
    data: {
      allowedResources: [
        {
          // TODO: What PermissionResource should be used
          resource: PermissionResource.All
        }
      ],
      directAllowAccessLineManager: true,
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'succession-management-support',
    loadChildren: () => import('./succession-management-support/succession-management-support.routes'),
    canActivate: [authGuard, canActivateRoleGuard()],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'incentive-support',
    loadChildren: () => import('@coin/customer/incentive-support/feature').then(m => m.INCENTIVE_SUPPORT_ROUTES),
    canActivate: [authGuard, canActivateRoleGuard(), environmentGuard],
    data: {
      frontendType: FrontendType.customer,
      featureFlag: 'incentiveSupport'
    }
  },
  {
    path: 'service-provider',
    component: ServiceProviderListComponent
  },
  {
    path: 'helpdesk',
    canActivate: [redirectGuard],
    children: [],
    data: {
      externalUrl: environment.helpdeskUrl
    }
  },
  {
    path: 'ecl',
    loadChildren: () => import('./employment-condition-library/employment-condition-library.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.EmploymentConditionLibraryUser
        },
        {
          resource: PermissionResource.EmploymentConditionLibraryAdmin
        },
        {
          resource: PermissionResource.EmploymentConditionLibraryRestricted
        },
        {
          resource: PermissionResource.EmploymentConditionLibraryGenerator
        }
      ])
    ],
    data: {
      frontendType: FrontendType.customer
    }
  },
  {
    path: 'exceptional-recognitions',
    loadChildren: () =>
      import('@coin/customer/exceptional-recognitions/feature').then(m => m.EXCEPTIONAL_RECOGNITIONS_ROUTES),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.ExceptionalRecognitions
        }
      ])
    ],
    data: {
      frontendType: FrontendType.customer,
      featureFlag: 'exceptionalRecognitions'
    }
  },
  {
    path: 'restructuring',
    loadChildren: () => import('./restructuring/restructuring.routes'),
    canActivate: [authGuard, customerRoleGuard, environmentGuard],
    data: {
      allowedResources: [
        {
          resource: PermissionResource.RestructuringAdmin
        },
        {
          resource: PermissionResource.RestructuringAdminRestricted
        },
        {
          resource: PermissionResource.RestructuringHr
        },
        {
          resource: PermissionResource.RestructuringConditionLibraryAdmin
        }
      ],
      frontendType: FrontendType.customer,
      featureFlag: 'restructuring'
    }
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent
  },
  {
    path: 'fallback',
    component: NoBackendScreenComponent
  },
  {
    path: '**',
    loadComponent: () => import('./content-pages/content-pages.component').then(m => m.ContentPagesComponent),
    pathMatch: 'full'
  }
];
